import React, { useContext, useState, useRef, forwardRef, useEffect } from 'react';
import { Col, Button, Badge, Modal } from 'react-bootstrap';
import Logo from '../Logo/Logo';
import { Link } from 'react-router-dom';
import game from '../../util/game.util';
import { StarFill, Check, ArrowBarLeft, X, Windows } from 'react-bootstrap-icons';
import { GameContext } from '../../context/game.context';
import Slider, { createSliderWithTooltip } from 'rc-slider';
import LinkButton from '../LinkButton/LinkButton';

const SliderWithTooltip = createSliderWithTooltip(Slider);

const Question = props => {
    const gameContext = useContext(GameContext);
    const save = gameContext.save;

    const topic = props.match.params.topic;

    const onQuestion = save[topic].onQuestion;
    const question = game[topic].questions[onQuestion];

    // Don't remember question progress on refresh
    const [part, setPart] = useState(0); // save[topic].onPart
    const [americansAgree, setAmericansAgree] = useState(50); // save[topic].answers[onQuestion].americansAgree ?? 50
    const [democratsAgree, setDemocratsAgree] = useState(50); // save[topic].answers[onQuestion].democratsAgree ?? 50
    const [republicansAgree, setRepublicansAgree] = useState(50); // save[topic].answers[onQuestion].republicansAgree ?? 50
    const [americanPercentButtonEnabled, setAmericanPercentButtonEnabled] = useState(true); // false
    const [democratPercentButtonEnabled, setDemocratPercentButtonEnabled] = useState(true); // false
    const [republicanPercentButtonEnabled, setRepublicanPercentButtonEnabled] = useState(true); // false
    const [moreInfoModalShown, setMoreInfoModalShown] = useState(false);

    const refs = useRef([]);

    useEffect(() => {
        if (part && part > 0 && refs.current[part]) {
            window.scrollTo({ top: refs.current[part].getBoundingClientRect().top + window.scrollY - 65, behavior: 'smooth' });
        }
    }, [part]);

    function answerPart(partAnswered, value) {
        let key;
        switch (partAnswered) {
            case 0:
                key = 'youAgree';
                break;
            case 1:
                key = 'americansAgree';
                break;
            case 2:
                key = 'democratsAgree';
                break;
            case 3:
                key = 'republicansAgree';
                break;
        }

        setPart(partAnswered + 1);
        save[topic].onPart = partAnswered + 1;
        save[topic].answers[onQuestion][key] = value;
        gameContext.updateSave({ save });
    }

    function percentFormatter(v) {
        return `${v}%`;
    }

    return (
        <Col lg="6" sm className='p-3'>
            <Logo />
            <LinkButton to="/" variant="light" className="mb-3"><ArrowBarLeft />Back</LinkButton>
            {/* <div className="question-icon">{game[topic].icon}</div> */}
            <h1 className="text-center mb-2">{topic}</h1>
            <h5 className='question-progress'>
                <Badge
                    bg="primary"
                    pill
                    className="mx-auto d-block mb-3"
                >
                    Question {onQuestion + 1}/{game[topic].questions.length}
                </Badge>
            </h5>
            <div>
                <p className="text-center mt-5"><strong>Consider the following statement:</strong></p>
                <div className="sticky-top mt-3 mb-5 quote-container">
                    <h5 className="quote">
                        {question.statement}
                    </h5>
                    <Button variant='success' className='more-info-button' onClick={() => setMoreInfoModalShown(true)}>Additional Context</Button>
                </div>
                <Part currentPart={part} part={0} ref={el => refs.current[0] = el}>
                    <p className="text-center"><strong>Do you agree or disagree?</strong></p>
                    <div className="text-center">
                        <Button variant="success" size="lg" style={{ width: '143px' }} className="me-4" onClick={() => answerPart(0, true)}>Agree</Button>
                        <Button variant="danger" size="lg" onClick={() => answerPart(0, false)}>Disagree</Button>
                    </div>
                </Part>
                <Part currentPart={part} part={1} ref={el => refs.current[1] = el}>
                    <p className="text-center"><strong>What percent of <span className="highlight-info">Americans</span> do you think agree ?</strong></p>
                    <SliderWithTooltip className="rc-info mb-4" tipFormatter={percentFormatter} defaultValue={americansAgree} onChange={(v) => { setAmericansAgree(v) }} onBeforeChange={() => setAmericanPercentButtonEnabled(true)} />
                    <div className="text-center"><Button variant="info" size="lg" disabled={!americanPercentButtonEnabled} onClick={() => answerPart(1, americansAgree)}>{americansAgree}% Agree</Button></div>
                </Part>
                <Part currentPart={part} part={2} ref={el => refs.current[2] = el}>
                    <p><strong>What percent of <span className="highlight-primary">Democrats</span> do you think agree ?</strong></p>
                    <SliderWithTooltip className="rc-primary mb-4" tipFormatter={percentFormatter} defaultValue={democratsAgree} onChange={(v) => { setDemocratsAgree(v) }} onBeforeChange={() => setDemocratPercentButtonEnabled(true)} />
                    <div className="text-center"><Button variant="primary" size="lg" disabled={!democratPercentButtonEnabled} onClick={() => answerPart(2, democratsAgree)}>{democratsAgree}% Agree</Button></div>
                </Part>
                <Part currentPart={part} part={3} ref={el => refs.current[3] = el}>
                    <p><strong>What percent of <span className="highlight-secondary">Republicans</span> do you think agree ?</strong></p>
                    <SliderWithTooltip className="rc-secondary mb-4" tipFormatter={percentFormatter} defaultValue={republicansAgree} onChange={(v) => { setRepublicansAgree(v) }} onBeforeChange={() => setRepublicanPercentButtonEnabled(true)} />
                    <div className="text-center"><Button variant="secondary" size="lg" disabled={!republicanPercentButtonEnabled} onClick={() => answerPart(3, republicansAgree)}>{republicansAgree}% Agree</Button></div>
                </Part>
            </div>
            <Part currentPart={part} part={4} ref={el => refs.current[2] = el} className="sticky-top">
                <h2 className="text-center">Results</h2>
                <p><strong>{save[topic].answers[onQuestion].youAgree === question.americansAgree >= 50 ? 'Like you' : 'Turns out'}, most Americans {question.americansAgree >= 50 ? 'agree' : 'disagree'}</strong> that {question.statement[0].toLowerCase() + question.statement.slice(1)}</p>

                <p><strong>Here's what you guessed—and what the polls show:</strong></p>

                <Bars agree={{ americansAgree, question }} /> {/* democratsAgree, republicansAgree, */}
                {/* This does not work anymore now that there are negative questions */}
                {/* <ul>
                    <li><Result guess={americansAgree} real={question.americansAgree} identity="Americans" /></li>
                    <li><Result guess={democratsAgree} real={question.democratsAgree} identity="Democrats" /></li>
                    <li><Result guess={republicansAgree} real={question.republicansAgree} identity="Republicans" /></li>
                </ul> */}
                <div className="text-center">
                    {onQuestion < game[topic].questions.length - 1 ?
                        <LinkButton
                            to={`/topic/${topic}/${save[topic].onQuestion + 1}`}
                            variant="light"
                            size="lg"
                            onClick={() => {
                                save[topic].onQuestion++;
                                save[topic].onPart = 0;
                                gameContext.updateSave({ save });
                            }}>
                            Next Question
                        </LinkButton>
                        : <LinkButton
                            to={`/topic/${topic}/results`}
                            variant="light"
                            size="lg"
                            className="mb-4"
                            onClick={() => {
                                save[topic].onQuestion++;
                                save[topic].onResults = true;
                                gameContext.updateSave({ save });
                            }}
                        >
                            Results Summary
                        </LinkButton>
                    }
                </div>
            </Part>
            <div><MoreInfoModal show={moreInfoModalShown} onShowChange={show => setMoreInfoModalShown(show)} question={question} /></div>
        </Col>
    );
}

export default Question;

const Part = forwardRef((props, ref) => {
    return <div ref={ref} className={'part' + (props.currentPart >= props.part ? ' part-visible' : '') + (props.currentPart >= props.part + 1 ? ' part-complete' : '') + ' ' + props.className}>{props.children}</div>;
});

const Result = props => {
    const diff = Math.abs(props.guess - props.real);
    let identityHighlight = '';
    switch (props.identity) {
        case 'Democrats':
            identityHighlight = 'highlight-primary'
            break;
        case 'Republicans':
            identityHighlight = 'highlight-secondary'
            break;
        case 'Americans':
            identityHighlight = 'highlight-info'
            break;
    }
    switch (true) {
        case (diff === 0):
            return <p><strong>Congratulations. You nailed it!</strong> You know <strong>exactly</strong> how much <span className={identityHighlight}>{props.identity}</span> agree.</p>;
        case (diff <= 10):
            return <p><strong>Congratulations!</strong> You have a <strong>excellent grasp</strong> of how much <span className={identityHighlight}>{props.identity}</span> agree.</p>;
        case ((props.guess - props.real) > 10):
            return <p><strong>Nice Try!</strong> While they still agree, <span className={identityHighlight}>{props.identity}</span> do not agree as much as you think.</p>;
        default:
            return <p><strong>Bet you're surprised!</strong> Turns out <span className={identityHighlight}>{props.identity}</span> agree more than you think!</p>;
    }
}

const Bars = props => {
    return (
        <div className="bars">
            <div className="bargroup">
                <div className="bar" style={{ height: props.agree.americansAgree + '%', background: 'linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), var(--bs-info)' }}><span className="label">Your Guess</span><span className="percent">{props.agree.americansAgree + '%'}</span></div>
            </div>
            <div className="bargroup">
                <div className="bar" style={{ height: props.agree.question.americansAgree + '%', background: 'var(--gradient)' }}><span className="label">All Americans</span><span className="percent">{props.agree.question.americansAgree + '%'}</span></div>
            </div>
            <div className="bargroup">
                <div className="bar" style={{ height: props.agree.question.democratsAgree + '%', background: 'var(--bs-primary)' }}><span className="label">Democrats</span><span className="percent">{props.agree.question.democratsAgree + '%'}</span></div>
            </div>
            <div className="bargroup">
                <div className="bar" style={{ height: props.agree.question.republicansAgree + '%', background: 'var(--bs-secondary)' }}><span className="label">Republicans</span><span className="percent">{props.agree.question.republicansAgree + '%'}</span></div>
            </div>
        </div>

    );
}

const MoreInfoModal = props => {
    return (
        <Modal show={props.show} onHide={() => props.onShowChange(false)} size="lg" centered scrollable>
            <Modal.Header closeButton style={{ border: 0 }}>
                {/* <Modal.Title>Modal title</Modal.Title> */}
            </Modal.Header>

            <Modal.Body>
                {/* <p>{props.question.context}</p> */}
                <h2>Arguments For</h2>
                {props.question.contextFor}
                <h2>Arguments Against</h2>
                {props.question.contextAgainst}
            </Modal.Body>
        </Modal>
    );
}
